/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { playbooksApi, mattersApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import HelpButton from "@/components/HelpButton";
import {
    Plus, Play, Wrench, ListChecks, BookOpen, CheckCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Playbooks() {
    return (
        <div className="p-4 md:p-8 max-w-[1300px] mx-auto" data-testid="playbooks-page">
            <div className="rule-line pt-3 mb-6">
                <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-1">Knowledge</div>
                <div className="flex items-center gap-2">
                    <h1 className="font-serif text-4xl tracking-tight">Playbooks</h1>
                    <HelpButton topic="playbooks" testid="help-playbooks" />
                </div>
                <p className="text-sm text-muted-foreground mt-1">Repeatable matter-type recipes. Apply to any matter — generates dated tasks with the right offsets.</p>
            </div>
            <Tabs defaultValue="system">
                <TabsList>
                    <TabsTrigger value="system" data-testid="playbooks-tab-system"><BookOpen size={13} className="mr-1" /> System</TabsTrigger>
                    <TabsTrigger value="custom" data-testid="playbooks-tab-custom"><Wrench size={13} className="mr-1" /> My playbooks</TabsTrigger>
                </TabsList>
                <TabsContent value="system" className="pt-4"><SystemPlaybooks /></TabsContent>
                <TabsContent value="custom" className="pt-4"><CustomPlaybooks /></TabsContent>
            </Tabs>
        </div>
    );
}

function SystemPlaybooks() {
    const [list, setList] = useState([]);
    useEffect(() => { playbooksApi.listSystem().then(setList).catch(() => {}); }, []);
    if (!list.length) return <div className="text-sm text-muted-foreground p-4">Loading…</div>;
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="system-playbooks-list">
            {list.map((p) => <PlaybookCard key={p.key} pb={p} system />)}
        </div>
    );
}

function CustomPlaybooks() {
    const [list, setList] = useState([]);
    const [open, setOpen] = useState(false);
    const load = async () => setList(await playbooksApi.list().catch(() => []));
    useEffect(() => { load(); }, []);
    return (
        <div className="space-y-4">
            <div className="flex justify-end">
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild><Button data-testid="playbook-new-btn"><Plus size={14} /> New playbook</Button></DialogTrigger>
                    <DialogContent className="max-w-3xl">
                        <DialogHeader>
                            <DialogTitle>New playbook</DialogTitle>
                            <DialogDescription>Define steps with day-offsets from start. Each step becomes a dated task when applied.</DialogDescription>
                        </DialogHeader>
                        <PlaybookForm onDone={() => { setOpen(false); load(); }} />
                    </DialogContent>
                </Dialog>
            </div>
            {!list.length ? (
                <div className="border border-dashed border-border rounded-md p-8 text-sm text-muted-foreground text-center">No custom playbooks yet. The system playbooks above cover most common scenarios — duplicate one of those if you want to customise.</div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {list.map((p) => <PlaybookCard key={p.id} pb={{ ...p, key: p.id }} system={false} onChange={load} />)}
                </div>
            )}
        </div>
    );
}

function PlaybookCard({ pb, system, onChange }) {
    const [applyOpen, setApplyOpen] = useState(false);
    return (
        <div className="border border-border bg-white rounded-md p-3" data-testid={`playbook-${pb.key}`}>
            <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{pb.name}</span>
                        <span className="chip chip-doc text-xs">{pb.matter_type}</span>
                        {system && <span className="chip chip-ai text-xs">system</span>}
                    </div>
                    {pb.summary && <div className="text-sm text-muted-foreground mt-1">{pb.summary}</div>}
                    <div className="text-xs text-muted-foreground mt-1 inline-flex items-center gap-1">
                        <ListChecks size={11} /> {(pb.steps || []).length} step(s)
                    </div>
                </div>
                <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
                    <DialogTrigger asChild>
                        <Button size="sm" data-testid={`playbook-apply-${pb.key}`}><Play size={12} weight="fill" /> Apply</Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Apply "{pb.name}"</DialogTitle>
                            <DialogDescription>Choose the matter and a start date. Tasks will be created at the right offsets.</DialogDescription>
                        </DialogHeader>
                        <ApplyForm pb={pb} system={system} onDone={() => setApplyOpen(false)} />
                    </DialogContent>
                </Dialog>
            </div>
            <details className="mt-3 border-t border-border pt-2">
                <summary className="text-xs text-muted-foreground cursor-pointer">Show steps</summary>
                <ol className="list-decimal list-inside mt-2 space-y-1 text-sm">
                    {(pb.steps || []).map((s, i) => (
                        <li key={i} className="border-b border-border/30 py-1">
                            <span className="font-medium">{s.title}</span>
                            <span className="text-xs text-muted-foreground ml-2">
                                {s.priority && <span className="chip text-[10px] mr-1">{s.priority}</span>}
                                day {s.days_offset >= 0 ? "+" : ""}{s.days_offset}
                                {s.category && <span className="ml-2 italic">· {s.category.replace(/_/g, " ")}</span>}
                            </span>
                        </li>
                    ))}
                </ol>
            </details>
        </div>
    );
}

function ApplyForm({ pb, system, onDone }) {
    const [matters, setMatters] = useState([]);
    const [matterId, setMatterId] = useState("");
    const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    useEffect(() => { mattersApi.list().then(setMatters).catch(() => {}); }, []);

    const submit = async () => {
        if (!matterId) { toast.error("Pick a matter"); return; }
        setBusy(true);
        try {
            const payload = system ? { matter_id: matterId, playbook_key: pb.key, start_date: startDate }
                                    : { matter_id: matterId, playbook_id: pb.key, start_date: startDate };
            const r = await playbooksApi.apply(payload);
            setResult(r);
            toast.success(`${r.tasks_created} task(s) created`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
        setBusy(false);
    };

    if (result) {
        return (
            <div className="space-y-2" data-testid="apply-result">
                <div className="flex items-center gap-2 text-emerald-700"><CheckCircle size={16} weight="fill" /> Created {result.tasks_created} task(s)</div>
                <div className="text-sm">Find them on the Tasks page or the matter's Workflow tab.</div>
                <div className="flex justify-end pt-2">
                    <Button variant="outline" onClick={onDone}>Close</Button>
                </div>
            </div>
        );
    }
    return (
        <div className="space-y-3" data-testid="apply-form">
            <div>
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">Matter</div>
                <Select value={matterId} onValueChange={setMatterId}>
                    <SelectTrigger data-testid="apply-matter"><SelectValue placeholder="Pick a matter…" /></SelectTrigger>
                    <SelectContent>{matters.map((m) => <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>)}</SelectContent>
                </Select>
            </div>
            <div>
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">Start date (offsets are measured from here)</div>
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="apply-start" />
            </div>
            <div className="flex justify-end"><Button onClick={submit} disabled={busy} data-testid="apply-submit">{busy ? "Applying…" : "Apply playbook"}</Button></div>
        </div>
    );
}

function PlaybookForm({ onDone }) {
    const [v, setV] = useState({ name: "", summary: "", matter_type: "any", steps_raw: "Step 1 — day 0 — priority medium\nStep 2 — day 7 — priority high" });
    const parseSteps = (raw) => raw.split(/\n+/).map((line) => {
        const dayMatch = line.match(/day\s*([-+]?\d+)/i);
        const prMatch = line.match(/priority\s+(low|medium|high|urgent)/i);
        const title = line.split("—")[0].trim();
        if (!title) return null;
        return {
            title,
            days_offset: dayMatch ? parseInt(dayMatch[1], 10) : 0,
            priority: prMatch ? prMatch[1].toLowerCase() : "medium",
        };
    }).filter(Boolean);
    const submit = async () => {
        const steps = parseSteps(v.steps_raw);
        if (!v.name) { toast.error("Name required"); return; }
        if (!steps.length) { toast.error("At least one step required"); return; }
        try {
            await playbooksApi.create({ name: v.name, summary: v.summary, matter_type: v.matter_type, steps });
            toast.success("Playbook saved");
            onDone?.();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };
    return (
        <div className="space-y-2" data-testid="playbook-form">
            <Input placeholder="Playbook name" value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} data-testid="pb-name" />
            <Textarea rows={2} placeholder="Summary" value={v.summary} onChange={(e) => setV({ ...v, summary: e.target.value })} />
            <Input placeholder="Matter type (e.g. ni_138, civil_suit, any)" value={v.matter_type} onChange={(e) => setV({ ...v, matter_type: e.target.value })} />
            <div>
                <div className="text-xs text-muted-foreground mb-1">Steps — one per line. Format: <code>Step title — day &lt;N&gt; — priority &lt;low|medium|high|urgent&gt;</code></div>
                <Textarea rows={10} value={v.steps_raw} onChange={(e) => setV({ ...v, steps_raw: e.target.value })} className="font-mono text-xs" data-testid="pb-steps" />
            </div>
            <div className="flex justify-end pt-1"><Button onClick={submit} data-testid="pb-submit">Save playbook</Button></div>
        </div>
    );
}
